import { guaranteeHash } from '~/_shared/components/colorPicker/colorPicker.helpers';
import type { ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';

/**
 * Utility functions for updating BoundaryTerritoryGroup objects in a type-safe and maintainable way.
 * These functions use immutable update patterns to avoid deeply nested object spreading.
 */

export const updateBoundaryTerritoryGroupLineWidth = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
  lineWidth: number
): BoundaryTerritoryGroup => ({
  ...boundaryTerritoryGroup,
  settings: {
    ...boundaryTerritoryGroup.settings,
    style: {
      ...boundaryTerritoryGroup.settings.style,
      lineWidth,
    },
  },
});

export const updateBoundaryTerritoryGroupLineColor = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
  color: ColorResult
): BoundaryTerritoryGroup => ({
  ...boundaryTerritoryGroup,
  settings: {
    ...boundaryTerritoryGroup.settings,
    style: {
      ...boundaryTerritoryGroup.settings.style,
      lineColor: guaranteeHash(color.hex),
    },
  },
});

export const updateBoundaryTerritoryGroupSetting = <K extends keyof BoundaryTerritoryGroup['settings']>(
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
  settingKey: K,
  value: BoundaryTerritoryGroup['settings'][K]
): BoundaryTerritoryGroup => ({
  ...boundaryTerritoryGroup,
  settings: {
    ...boundaryTerritoryGroup.settings,
    [settingKey]: value,
  },
});

/**
 * Creates an updater function for boolean toggle settings
 */
export const createBoundaryTerritoryGroupToggleUpdater = <K extends keyof BoundaryTerritoryGroup['settings']>(
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
  settingKey: BoundaryTerritoryGroup['settings'][K] extends boolean ? K : never
) => {
  return (value?: boolean): BoundaryTerritoryGroup => {
    const currentValue = boundaryTerritoryGroup.settings[settingKey];
    const newValue = value ?? !currentValue;

    return updateBoundaryTerritoryGroupSetting(boundaryTerritoryGroup, settingKey, newValue as BoundaryTerritoryGroup['settings'][K]);
  };
};
